import NumberInput from "../../../components/NumberInput";
import React, { useMemo, useState } from "react";
import { FieldFeedback, RequiredMark } from "../../../components/FormValidation";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules } from "../../../utils/helpers/formValidation";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import ModalShell from "../../CashBankPage/components/ModalShell";
import AkasSelect from "../../../components/AkasSelect";
import { formatDate, localDate } from "../constants";

const randomKey = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const FormModal = ({ asset, categories = [], accounts = [], suppliers = [], saving, onClose, onSave }) => {
  const isEdit = Boolean(asset?.id);
  const initialCategory = categories.find((item) => Number(item.id) === Number(asset?.category_id)) || categories[0];
  const [form, setForm] = useState({
    name: asset?.name || "",
    category_id: asset?.category_id || initialCategory?.id || "",
    financial_account_id: asset?.financial_account_id || accounts[0]?.id || "",
    supplier_id: asset?.supplier_id || "",
    supplier_name: asset?.supplier_name || "",
    acquisition_cost: isEdit ? String(asset?.acquisition_cost || "") : "",
    residual_value: isEdit ? String(asset?.residual_value || 0) : "0",
    useful_life_months: String(asset?.useful_life_months || initialCategory?.default_useful_life_months || 36),
    acquired_at: asset?.acquired_at ? String(asset.acquired_at).slice(0, 10) : localDate(),
    available_for_use_at: asset?.available_for_use_at ? String(asset.available_for_use_at).slice(0, 10) : localDate(),
    location: asset?.location || "",
    serial_number: asset?.serial_number || "",
    note: asset?.note || "",
  });
  const selectedAccount = useMemo(
    () => accounts.find((account) => Number(account.id) === Number(form.financial_account_id)),
    [accounts, form.financial_account_id],
  );
  const depreciableAmount = Math.max(0, Number(form.acquisition_cost || 0) - Number(form.residual_value || 0));
  const monthlyDepreciation = Number(form.useful_life_months || 0) > 0
    ? Math.round(depreciableAmount / Number(form.useful_life_months))
    : 0;
  const schema = useMemo(() => ({
    name: [validationRules.required("Nama aset wajib diisi."), validationRules.maxLength(160, "Nama aset maksimal 160 karakter.")],
    category_id: [validationRules.required("Kategori aset wajib dipilih.")],
    supplier_name: [validationRules.maxLength(160, "Nama supplier maksimal 160 karakter.")],
    location: [validationRules.maxLength(160, "Lokasi maksimal 160 karakter.")],
    serial_number: [validationRules.maxLength(120, "Nomor seri maksimal 120 karakter.")],
    note: [validationRules.maxLength(1000, "Keterangan maksimal 1.000 karakter.")],
    ...(!isEdit ? {
      financial_account_id: [validationRules.required("Akun pembayaran wajib dipilih.")],
      acquisition_cost: [
        validationRules.required("Harga perolehan wajib diisi."),
        validationRules.number(),
        validationRules.integer("Harga perolehan harus berupa rupiah bulat."),
        validationRules.min(1, "Harga perolehan minimal Rp1."),
        validationRules.custom((value) => Number(value || 0) > Number(selectedAccount?.current_balance || 0)
          ? "Harga aset melebihi saldo akun yang tersedia."
          : ""),
      ],
      residual_value: [
        validationRules.number(),
        validationRules.integer("Nilai residu harus berupa rupiah bulat."),
        validationRules.min(0, "Nilai residu tidak boleh negatif."),
        validationRules.custom((value) => Number(value || 0) >= Number(form.acquisition_cost || 0)
          ? "Nilai residu harus lebih kecil dari harga perolehan."
          : ""),
      ],
      useful_life_months: [
        validationRules.required("Umur manfaat wajib diisi."),
        validationRules.integer("Umur manfaat harus berupa jumlah bulan."),
        validationRules.min(1, "Umur manfaat minimal 1 bulan."),
        validationRules.max(600, "Umur manfaat maksimal 600 bulan."),
      ],
      acquired_at: [validationRules.required("Tanggal perolehan wajib diisi.")],
      available_for_use_at: [
        validationRules.required("Tanggal mulai digunakan wajib diisi."),
        validationRules.custom((value) => value && form.acquired_at && value < form.acquired_at
          ? "Tanggal mulai digunakan tidak boleh sebelum tanggal perolehan."
          : ""),
      ],
    } : {}),
  }), [form.acquired_at, form.acquisition_cost, isEdit, selectedAccount?.current_balance]);
  const validation = useFormValidation({ values: form, schema, formId: "fixed-asset-form" });

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const changeCategory = (value) => {
    const category = categories.find((item) => Number(item.id) === Number(value));
    setForm((current) => ({
      ...current,
      category_id: value,
      ...(!isEdit && category ? { useful_life_months: String(category.default_useful_life_months || 36) } : {}),
    }));
  };
  const changeSupplier = (value) => {
    const supplier = suppliers.find((item) => Number(item.id) === Number(value));
    setForm((current) => ({ ...current, supplier_id: value, supplier_name: supplier?.name || "" }));
  };
  const save = (event) => {
    event.preventDefault();
    if (!validation.validateAll()) return;

    const metadata = {
      name: form.name.trim(),
      category_id: Number(form.category_id),
      supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
      supplier_name: form.supplier_name.trim() || null,
      location: form.location.trim() || null,
      serial_number: form.serial_number.trim() || null,
      note: form.note.trim() || null,
    };
    onSave(isEdit ? metadata : {
      ...metadata,
      financial_account_id: Number(form.financial_account_id),
      acquisition_cost: Number(form.acquisition_cost),
      residual_value: Number(form.residual_value || 0),
      useful_life_months: Number(form.useful_life_months),
      acquired_at: form.acquired_at,
      available_for_use_at: form.available_for_use_at,
      idempotency_key: randomKey(),
    });
  };

  return (
    <ModalShell
      eyebrow={isEdit ? "EDIT INFORMASI ASET" : "PENCATATAN ASET"}
      title={isEdit ? "Edit informasi aset" : "Tambah aset"}
      subtitle={isEdit
        ? "Nilai transaksi dan jadwal penyusutan dikunci untuk menjaga audit."
        : "Saldo Kas & Bank berkurang saat aset dicatat; laba berkurang bertahap melalui penyusutan."}
      onClose={onClose}
      onSubmit={save}
      saving={saving}
      submitText={isEdit ? "Simpan perubahan" : "Simpan aset"}
      size="xl">
      {isEdit ? (
        <div className="fixed-asset-locked-grid" aria-label="Informasi aset yang dikunci">
          <div><span>Harga perolehan</span><strong>{formatIDR(asset.acquisition_cost)}</strong></div>
          <div><span>Akun pembayaran</span><strong>{asset.account_name || "-"}</strong></div>
          <div><span>Mulai digunakan</span><strong>{formatDate(asset.available_for_use_at)}</strong></div>
          <div><span>Umur manfaat</span><strong>{asset.useful_life_months} bulan</strong></div>
          <p>Koreksi nilai aset dilakukan melalui pembatalan sebelum penyusutan pertama diakui.</p>
        </div>
      ) : null}

      <div className="row g-3">
        <div className="col-md-7">
          <label className="form-label">Nama aset <RequiredMark /></label>
          <input autoFocus className={validation.inputClass("name")} {...validation.fieldProps("name")} value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="Contoh: Mesin Espresso 2 Group" />
          <FieldFeedback formId="fixed-asset-form" name="name" error={validation.errors.name} />
        </div>
        <div className="col-md-5">
          <label className="form-label">Kategori <RequiredMark /></label>
          <AkasSelect className={validation.inputClass("category_id", "form-select")} {...validation.fieldProps("category_id")} value={form.category_id} onChange={(event) => changeCategory(event.target.value)}>
            <option value="">Pilih kategori</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
          </AkasSelect>
          <FieldFeedback formId="fixed-asset-form" name="category_id" error={validation.errors.category_id} />
        </div>

        {!isEdit ? (
          <>
            <div className="col-md-6">
              <label className="form-label">Dibayar dari <RequiredMark /></label>
              <AkasSelect className={validation.inputClass("financial_account_id", "form-select")} {...validation.fieldProps("financial_account_id")} value={form.financial_account_id} onChange={(event) => update("financial_account_id", event.target.value)}>
                <option value="">Pilih akun Kas & Bank</option>
                {accounts.map((account) => <option key={account.id} value={account.id}>{account.name} · {formatIDR(account.current_balance)}</option>)}
              </AkasSelect>
              <FieldFeedback formId="fixed-asset-form" name="financial_account_id" error={validation.errors.financial_account_id} />
            </div>
            <div className="col-md-3 col-6">
              <label className="form-label">Tanggal perolehan <RequiredMark /></label>
              <input type="date" className={validation.inputClass("acquired_at")} {...validation.fieldProps("acquired_at")} value={form.acquired_at} onChange={(event) => update("acquired_at", event.target.value)} />
              <FieldFeedback formId="fixed-asset-form" name="acquired_at" error={validation.errors.acquired_at} />
            </div>
            <div className="col-md-3 col-6">
              <label className="form-label">Mulai digunakan <RequiredMark /></label>
              <input type="date" min={form.acquired_at} className={validation.inputClass("available_for_use_at")} {...validation.fieldProps("available_for_use_at")} value={form.available_for_use_at} onChange={(event) => update("available_for_use_at", event.target.value)} />
              <FieldFeedback formId="fixed-asset-form" name="available_for_use_at" error={validation.errors.available_for_use_at} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Harga perolehan <RequiredMark /></label>
              <div className="input-group"><span className="input-group-text">Rp</span><NumberInput type="number" min="1" step="1" inputMode="numeric" className={validation.inputClass("acquisition_cost")} {...validation.fieldProps("acquisition_cost")} value={form.acquisition_cost} onChange={(event) => update("acquisition_cost", event.target.value)} placeholder="0" /></div>
              <FieldFeedback formId="fixed-asset-form" name="acquisition_cost" error={validation.errors.acquisition_cost} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Nilai residu</label>
              <div className="input-group"><span className="input-group-text">Rp</span><NumberInput type="number" min="0" step="1" inputMode="numeric" className={validation.inputClass("residual_value")} {...validation.fieldProps("residual_value")} value={form.residual_value} onChange={(event) => update("residual_value", event.target.value)} /></div>
              <FieldFeedback formId="fixed-asset-form" name="residual_value" error={validation.errors.residual_value} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Umur manfaat <RequiredMark /></label>
              <div className="input-group"><NumberInput type="number" min="1" max="600" step="1" className={validation.inputClass("useful_life_months")} {...validation.fieldProps("useful_life_months")} value={form.useful_life_months} onChange={(event) => update("useful_life_months", event.target.value)} /><span className="input-group-text">bulan</span></div>
              <FieldFeedback formId="fixed-asset-form" name="useful_life_months" error={validation.errors.useful_life_months} />
            </div>
          </>
        ) : null}

        <div className="col-md-6">
          <label className="form-label">Supplier</label>
          <AkasSelect className="form-select" value={form.supplier_id} onChange={(event) => changeSupplier(event.target.value)}>
            <option value="">Vendor satu kali / tidak dipilih</option>
            {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
          </AkasSelect>
        </div>
        <div className="col-md-6">
          <label className="form-label">Nama vendor</label>
          <input className={validation.inputClass("supplier_name")} {...validation.fieldProps("supplier_name")} value={form.supplier_name} disabled={Boolean(form.supplier_id)} onChange={(event) => update("supplier_name", event.target.value)} placeholder="Opsional" />
          <FieldFeedback formId="fixed-asset-form" name="supplier_name" error={validation.errors.supplier_name} />
        </div>
        <div className="col-md-6">
          <label className="form-label">Lokasi aset</label>
          <input className={validation.inputClass("location")} {...validation.fieldProps("location")} value={form.location} onChange={(event) => update("location", event.target.value)} placeholder="Contoh: Coffee Bar lantai 1" />
          <FieldFeedback formId="fixed-asset-form" name="location" error={validation.errors.location} />
        </div>
        <div className="col-md-6">
          <label className="form-label">Nomor seri</label>
          <input className={validation.inputClass("serial_number")} {...validation.fieldProps("serial_number")} value={form.serial_number} onChange={(event) => update("serial_number", event.target.value)} placeholder="Opsional" />
          <FieldFeedback formId="fixed-asset-form" name="serial_number" error={validation.errors.serial_number} />
        </div>
        <div className="col-12">
          <label className="form-label">Keterangan</label>
          <textarea rows="3" maxLength="1000" className={validation.inputClass("note")} {...validation.fieldProps("note")} value={form.note} onChange={(event) => update("note", event.target.value)} placeholder="Nomor invoice, kondisi awal, atau catatan internal..." />
          <FieldFeedback formId="fixed-asset-form" name="note" error={validation.errors.note} />
        </div>

        {!isEdit ? (
          <div className="fixed-asset-preview">
            <div><span>Saldo tersedia</span><strong>{formatIDR(selectedAccount?.current_balance)}</strong></div>
            <div><span>Sisa saldo</span><strong>{formatIDR(Number(selectedAccount?.current_balance || 0) - Number(form.acquisition_cost || 0))}</strong></div>
            <div><span>Estimasi penyusutan</span><strong>{formatIDR(monthlyDepreciation)}/bulan</strong></div>
            <p>Metode garis lurus · penyusutan pertama diakui pada akhir bulan aset mulai digunakan.</p>
          </div>
        ) : null}
      </div>
    </ModalShell>
  );
};

export default FormModal;
